import { useState, useEffect, useRef, useCallback } from 'react';
import { dbService } from '../services/DatabaseService';
import {
  cloneQuestions,
  getConfiguredTemplateId,
  setConfiguredTemplateId,
  clearConfiguredTemplateId,
  normalizeCustomPageType,
  inferCustomPageTypeByIndex,
  resolvePageTitle,
  extractLegacySectionConfig,
  clampDefaultQuestionsPageOrder,
  createDefaultCustomPages
} from '../utils/questionnaireEditorUtils';
import {
  PAGE_TYPE_BASIS,
  PAGE_TYPE_FACH,
  DEFAULT_FORMAL_TEMPLATE_ID_STORAGE_KEY,
  DEFAULT_BASIS_TEMPLATE_ID_STORAGE_KEY,
  DEFAULT_FACH_TEMPLATE_ID_STORAGE_KEY,
  NAME_QUESTION_SEED
} from '../utils/questionnaireEditorConstants';

/**
 * Manages all 3 template systems (questionnaire, basic, default/formal),
 * template loading effects, modal state, and default template configuration.
 */
export function useEditorTemplates({
  questions,
  defaultQuestions, setDefaultQuestions,
  questionPages,
  applyEditorStructure,
  questionsWithPageMeta,
  error, setError,
  showNotification,
  // State setters for fields restored during template load
  setTitle,
  setIntroText,
  setOutroText,
  setCancelText,
  setCancelOutroText,
  setIntroWelcomeText,
  setIntroOverviewText,
  setIntroAfterSectionsText,
  setDefaultSectionTitle,
  setDefaultSectionDescription,
  setReviewSectionTitle,
  setReviewSectionDescription,
  setIntroSectionsText,
  setIntroTimerText,
  setIntroStartButtonText,
  setAutoSubmitEnabled,
  setAllowMultipleSubmissions,
  setDefaultQuestionsPageOrder,
  setScoringPrompt,
  setScoringTotalScore,
  setPageMaxScores,
  // Default values for reset
  defaultIntroText,
  defaultOutroText,
  // Current page state
  activePage,
  isBasisPageSelected,
  isFachPageSelected,
  normalizedDefaultQuestionsPageOrder,
  sectionsTextForSave,
  // Callback that returns current editor state for template saving
  getFullEditorState
}) {
  // Questionnaire templates
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Basic question templates
  const [basicQuestionTemplates, setBasicQuestionTemplates] = useState([]);
  const [basicTemplateName, setBasicTemplateName] = useState('');
  const [selectedBasicTemplateId, setSelectedBasicTemplateId] = useState('');

  // Fach question templates
  const [fachQuestionTemplates, setFachQuestionTemplates] = useState([]);
  const [fachTemplateName, setFachTemplateName] = useState('');
  const [selectedFachTemplateId, setSelectedFachTemplateId] = useState('');

  // Default/formal question templates
  const [defaultQuestionTemplates, setDefaultQuestionTemplates] = useState([]);
  const [defaultTemplateName, setDefaultTemplateName] = useState('');
  const [selectedDefaultTemplateId, setSelectedDefaultTemplateId] = useState('');

  // Default template IDs (configured via localStorage)
  const [defaultFormalTemplateId, setDefaultFormalTemplateId] = useState(() => getConfiguredTemplateId(DEFAULT_FORMAL_TEMPLATE_ID_STORAGE_KEY));
  const [defaultBasisTemplateId, setDefaultBasisTemplateId] = useState(() => getConfiguredTemplateId(DEFAULT_BASIS_TEMPLATE_ID_STORAGE_KEY));
  const [defaultFachTemplateId, setDefaultFachTemplateId] = useState(() => getConfiguredTemplateId(DEFAULT_FACH_TEMPLATE_ID_STORAGE_KEY));

  // Template confirmation modals
  const [showTemplateConfirmModal, setShowTemplateConfirmModal] = useState(false);
  const [pendingTemplateData, setPendingTemplateData] = useState(null);
  const [showDefaultTemplateConfirmModal, setShowDefaultTemplateConfirmModal] = useState(false);
  const [pendingDefaultTemplateData, setPendingDefaultTemplateData] = useState(null);

  const hasAutoLoadedDefaultTemplatesRef = useRef(false);

  // --- Load templates on mount ---
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const pocketbaseTemplates = await dbService.getTemplates();
        setTemplates(pocketbaseTemplates);
      } catch (error) {
        console.error('Error loading templates:', error);
        setTemplates([]);
      }
    };
    loadTemplates();
  }, []);

  useEffect(() => {
    const loadDefaultQuestionTemplates = async () => {
      try {
        const pocketbaseTemplates = await dbService.getDefaultQuestionTemplates();
        setDefaultQuestionTemplates(pocketbaseTemplates);
        if (defaultFormalTemplateId && !pocketbaseTemplates.some((template) => String(template.id) === String(defaultFormalTemplateId))) {
          clearConfiguredTemplateId(DEFAULT_FORMAL_TEMPLATE_ID_STORAGE_KEY);
          setDefaultFormalTemplateId('');
        }
      } catch (error) {
        console.error('Error loading default question templates:', error);
        setDefaultQuestionTemplates([]);
      }
    };
    loadDefaultQuestionTemplates();
  }, [defaultFormalTemplateId]);

  useEffect(() => {
    const loadBasicQuestionTemplates = async () => {
      try {
        const basisTemplates = await dbService.getBasicQuestionTemplates();
        setBasicQuestionTemplates(basisTemplates);
        if (defaultBasisTemplateId && !basisTemplates.some((template) => String(template.id) === String(defaultBasisTemplateId))) {
          clearConfiguredTemplateId(DEFAULT_BASIS_TEMPLATE_ID_STORAGE_KEY);
          setDefaultBasisTemplateId('');
        }
      } catch (error) {
        console.error('Error loading basic question templates:', error);
        setBasicQuestionTemplates([]);
      }
    };
    loadBasicQuestionTemplates();
  }, [defaultBasisTemplateId]);

  useEffect(() => {
    const loadFachQuestionTemplates = async () => {
      try {
        const fachTemplates = await dbService.getFachQuestionTemplates();
        setFachQuestionTemplates(fachTemplates);
        if (defaultFachTemplateId && !fachTemplates.some((template) => String(template.id) === String(defaultFachTemplateId))) {
          clearConfiguredTemplateId(DEFAULT_FACH_TEMPLATE_ID_STORAGE_KEY);
          setDefaultFachTemplateId('');
        }
      } catch (error) {
        console.error('Error loading fach question templates:', error);
        setFachQuestionTemplates([]);
      }
    };
    loadFachQuestionTemplates();
  }, [defaultFachTemplateId]);

  // --- Remap helper ---
  const remapTemplateQuestionsToPage = useCallback((templateQuestions = [], pageId) => {
    const idMap = new Map();
    const mappedQuestions = (templateQuestions || []).map((question, index) => {
      const newId = `q_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 7)}`;
      idMap.set(String(question.id), newId);

      const portableQuestion = { ...(question || {}) };
      delete portableQuestion.file;
      delete portableQuestion.pageId;
      delete portableQuestion.pageTitle;
      delete portableQuestion.pageDescription;
      delete portableQuestion.pageOrder;
      return {
        ...portableQuestion,
        id: newId,
        pageId
      };
    });

    return mappedQuestions.map((question) => {
      const dependencyId = question?.conditional?.dependsOn;
      if (!dependencyId) return question;

      const remappedDependencyId = idMap.get(String(dependencyId));
      if (!remappedDependencyId) {
        const { conditional, ...rest } = question;
        return rest;
      }

      return {
        ...question,
        conditional: {
          ...question.conditional,
          dependsOn: remappedDependencyId
        }
      };
    });
  }, []);

  // --- Questionnaire template CRUD ---
  const saveAsTemplate = async () => {
    const hasAnyQuestions = questions.length > 0 || defaultQuestions.length > 0;
    if (!templateName.trim() || !hasAnyQuestions) {
      setError("Bitte geben Sie einen Vorlagennamen ein und fügen Sie Fragen hinzu.");
      return;
    }

    try {
      const allTemplates = await dbService.getTemplates();
      const existingTemplate = allTemplates.find(t => t.name.trim().toLowerCase() === templateName.trim().toLowerCase());

      if (existingTemplate) {
        setPendingTemplateData({ existingTemplate, templateName });
        setShowTemplateConfirmModal(true);
        return;
      }

      await saveTemplateData(null);
    } catch (error) {
      console.error('Error saving template:', error);
      setError(`Fehler beim Speichern der Vorlage: ${error.message}`);
    }
  };

  const saveTemplateData = async (existingTemplate = null) => {
    try {
      const editorState = getFullEditorState ? getFullEditorState() : {};
      const sourceQuestionsWithPageMeta = questionsWithPageMeta(questions);
      const preparedQuestions = sourceQuestionsWithPageMeta.map((q) => {
        if (q.type === 'image') {
          return {
            ...q,
            file: null,
            imageDataUrl: null
          };
        }
        const { file, ...rest } = q;
        return rest;
      });

      const introPageData = {
        welcomeText: editorState.introWelcomeText || '',
        overviewText: editorState.introOverviewText || '',
        afterSectionsText: editorState.introAfterSectionsText || '',
        defaultSectionTitle: editorState.defaultSectionTitle || '',
        defaultSectionDescription: editorState.defaultSectionDescription || '',
        reviewSectionTitle: editorState.reviewSectionTitle || '',
        reviewSectionDescription: editorState.reviewSectionDescription || '',
        sectionsText: sectionsTextForSave,
        timerText: editorState.introTimerText || '',
        startButtonText: editorState.introStartButtonText || '',
        autoSubmitEnabled: editorState.autoSubmitEnabled || false,
        allowMultipleSubmissions: editorState.allowMultipleSubmissions || false,
        defaultQuestionsPageOrder: normalizedDefaultQuestionsPageOrder,
        scoringTotalScore: editorState.scoringTotalScore || 100,
        pageMaxScores: editorState.pageMaxScores || {},
        questionPages: questionPages.map((page, index) => ({
          id: page.id,
          title: resolvePageTitle(page.title, `Seite ${index + 1}`),
          description: page.description || '',
          order: index + 1,
          pageType: normalizeCustomPageType(page.pageType, inferCustomPageTypeByIndex(index))
        }))
      };

      const templateData = {
        name: templateName.trim(),
        title: editorState.title || '',
        questions: preparedQuestions,
        questionPages: introPageData.questionPages,
        defaultQuestions,
        defaultQuestionsPageOrder: normalizedDefaultQuestionsPageOrder,
        introText: editorState.introText || '',
        outroText: editorState.outroText || '',
        introWelcomeText: editorState.introWelcomeText || '',
        introOverviewText: editorState.introOverviewText || '',
        introAfterSectionsText: editorState.introAfterSectionsText || '',
        defaultSectionTitle: editorState.defaultSectionTitle || '',
        defaultSectionDescription: editorState.defaultSectionDescription || '',
        reviewSectionTitle: editorState.reviewSectionTitle || '',
        reviewSectionDescription: editorState.reviewSectionDescription || '',
        introSectionsText: sectionsTextForSave,
        introTimerText: editorState.introTimerText || '',
        introStartButtonText: editorState.introStartButtonText || '',
        cancelText: editorState.cancelText || '',
        cancelOutroText: editorState.cancelOutroText || '',
        autoSubmitEnabled: editorState.autoSubmitEnabled || false,
        allowMultipleSubmissions: editorState.allowMultipleSubmissions || false,
        intro_page: introPageData,
        scoring_prompt: editorState.scoringPrompt || '',
        qualification_prompt: existingTemplate?.qualification_prompt || '',
        analysis_prompt: existingTemplate?.analysis_prompt || ''
      };

      if (existingTemplate) {
        await dbService.updateTemplate(existingTemplate.id, templateData);
      } else {
        await dbService.saveTemplate(templateData);
      }

      const updatedTemplates = await dbService.getTemplates();
      setTemplates(updatedTemplates);
      setTemplateName('');
      const action = existingTemplate ? "überschrieben" : "gespeichert";
      showNotification(`Vorlage "${templateName}" wurde ${action}!`, 'success');
    } catch (error) {
      console.error('Error saving template:', error);
      setError(`Fehler beim Speichern der Vorlage: ${error.message}`);
    }
  };

  const loadFromTemplate = async (templateId) => {
    try {
      const allTemplates = await dbService.getTemplates();
      const template = allTemplates.find(t => t.id === templateId);

      if (template) {
        setTitle(template.title);
        const loadedPages = template.questionPages || template.intro_page?.questionPages || questionPages;
        {
          const idMap = new Map();
          const remapped = (template.questions || []).map((q, index) => {
            const newId = `q_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 7)}`;
            idMap.set(String(q.id), newId);
            return { ...q, id: newId };
          });
          const loadedQuestions = remapped.map((q) => {
            const dep = q?.conditional?.dependsOn;
            if (!dep) return q;
            const newDep = idMap.get(String(dep));
            if (!newDep) {
              const { conditional, ...rest } = q;
              return rest;
            }
            return { ...q, conditional: { ...q.conditional, dependsOn: newDep } };
          });
          applyEditorStructure(loadedQuestions, loadedPages);
        }
        const templateDefaultOrder = template.defaultQuestionsPageOrder ?? template.intro_page?.defaultQuestionsPageOrder;
        setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(templateDefaultOrder, loadedPages.length));
        setIntroText(template.introText || defaultIntroText);
        setOutroText(template.outroText || defaultOutroText);
        setCancelText(template.cancelText || 'Du hast gemerkt, dass wir vielleicht nicht die richtige Passung sind? Kein Problem — das ist für beide Seiten wichtig! Hier kannst du uns einfach mitteilen, dass du deine Bewerbung zurückziehen möchtest.');
        setCancelOutroText(template.cancelOutroText || 'Vielen Dank für deine Zeit! Du hast deine Bewerbung erfolgreich abgesagt. Falls du dich doch nochmal entscheidest, kannst du gerne eine neue Bewerbung einreichen.');

        if (template.intro_page) {
          const legacySectionConfig = extractLegacySectionConfig(template.intro_page.sectionsText || template.introSectionsText || '');
          setIntroWelcomeText(template.intro_page.welcomeText || template.introWelcomeText || '');
          setIntroOverviewText(template.intro_page.overviewText || template.introOverviewText || '');
          setIntroAfterSectionsText(template.intro_page.afterSectionsText || template.introAfterSectionsText || '');
          setDefaultSectionTitle(template.intro_page.defaultSectionTitle ?? template.defaultSectionTitle ?? legacySectionConfig.defaultSectionTitle);
          setDefaultSectionDescription(template.intro_page.defaultSectionDescription ?? template.defaultSectionDescription ?? legacySectionConfig.defaultSectionDescription);
          setReviewSectionTitle(template.intro_page.reviewSectionTitle ?? template.reviewSectionTitle ?? legacySectionConfig.reviewSectionTitle);
          setReviewSectionDescription(template.intro_page.reviewSectionDescription ?? template.reviewSectionDescription ?? legacySectionConfig.reviewSectionDescription);
          setIntroSectionsText(template.intro_page.sectionsText || template.introSectionsText || '');
          setIntroTimerText(template.intro_page.timerText || template.introTimerText || '');
          setIntroStartButtonText(template.intro_page.startButtonText || template.introStartButtonText || '');
          setAutoSubmitEnabled(template.intro_page.autoSubmitEnabled !== undefined ? template.intro_page.autoSubmitEnabled : (template.autoSubmitEnabled !== undefined ? template.autoSubmitEnabled : false));
          setAllowMultipleSubmissions(template.intro_page.allowMultipleSubmissions !== undefined
            ? template.intro_page.allowMultipleSubmissions
            : (template.allowMultipleSubmissions !== undefined ? template.allowMultipleSubmissions : false));
        } else {
          const legacySectionConfig = extractLegacySectionConfig(template.introSectionsText || '');
          setIntroWelcomeText(template.introWelcomeText || '');
          setIntroOverviewText(template.introOverviewText || '');
          setIntroAfterSectionsText(template.introAfterSectionsText || '');
          setDefaultSectionTitle(template.defaultSectionTitle ?? legacySectionConfig.defaultSectionTitle);
          setDefaultSectionDescription(template.defaultSectionDescription ?? legacySectionConfig.defaultSectionDescription);
          setReviewSectionTitle(template.reviewSectionTitle ?? legacySectionConfig.reviewSectionTitle);
          setReviewSectionDescription(template.reviewSectionDescription ?? legacySectionConfig.reviewSectionDescription);
          setIntroSectionsText(template.introSectionsText || '');
          setIntroTimerText(template.introTimerText || '');
          setIntroStartButtonText(template.introStartButtonText || '');
          setAutoSubmitEnabled(template.autoSubmitEnabled !== undefined ? template.autoSubmitEnabled : false);
          setAllowMultipleSubmissions(template.allowMultipleSubmissions !== undefined ? template.allowMultipleSubmissions : false);
        }

        const templateDefaults = (template.default_questions && Array.isArray(template.default_questions) && template.default_questions.length > 0)
          ? template.default_questions
          : (template.defaultQuestions && Array.isArray(template.defaultQuestions) && template.defaultQuestions.length > 0)
            ? template.defaultQuestions
            : null;
        if (templateDefaults) {
          const hasNameQuestion = templateDefaults.some((question) => question?.id === NAME_QUESTION_SEED.id);
          setDefaultQuestions(hasNameQuestion ? templateDefaults : [NAME_QUESTION_SEED, ...templateDefaults]);
        } else {
          setDefaultQuestions([NAME_QUESTION_SEED]);
        }
        if (setScoringPrompt && template.scoring_prompt !== undefined) {
          setScoringPrompt(template.scoring_prompt || '');
        }
        if (setScoringTotalScore) {
          const savedTotal = template.intro_page?.scoringTotalScore;
          if (savedTotal !== undefined) {
            const parsed = Number(savedTotal);
            setScoringTotalScore(Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : 100);
          }
        }
        if (setPageMaxScores) {
          const savedPageMaxScores = template.intro_page?.pageMaxScores;
          setPageMaxScores(savedPageMaxScores && typeof savedPageMaxScores === 'object' ? savedPageMaxScores : {});
        }
        setTemplateName(template.name);
        if (error) setError(null);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      setError('Fehler beim Laden der Vorlage');
    }
  };

  const applyTemplateToEmptyPages = async (templateId) => {
    if (!templateId) return;

    try {
      const allTemplates = await dbService.getTemplates();
      const template = allTemplates.find((entry) => entry.id === templateId);
      if (!template) return;

      const templatePagesRaw = Array.isArray(template.questionPages)
        ? template.questionPages
        : (Array.isArray(template.intro_page?.questionPages) ? template.intro_page.questionPages : []);
      const templatePages = [...templatePagesRaw].sort((a, b) => (a.order || 0) - (b.order || 0));
      const currentPages = [...questionPages].sort((a, b) => (a.order || 0) - (b.order || 0));

      const templateQuestions = Array.isArray(template.questions) ? template.questions : [];
      const nextQuestions = [...questions];

      currentPages.forEach((currentPage, index) => {
        const currentPageQuestions = questions.filter((question) => question.pageId === currentPage.id);
        const isCurrentPageEmpty = currentPageQuestions.length === 0;
        if (!isCurrentPageEmpty) return;

        const sourceTemplatePage = templatePages[index];
        if (!sourceTemplatePage?.id) return;

        const sourcePageQuestions = templateQuestions.filter(
          (question) => String(question.pageId) === String(sourceTemplatePage.id)
        );
        if (sourcePageQuestions.length === 0) return;

        const mappedQuestions = remapTemplateQuestionsToPage(sourcePageQuestions, currentPage.id);
        nextQuestions.push(...mappedQuestions);
      });

      const templateDefaultQuestions = Array.isArray(template.default_questions)
        ? template.default_questions
        : (Array.isArray(template.defaultQuestions) ? template.defaultQuestions : []);
      const hasOnlyNameSeed =
        Array.isArray(defaultQuestions) &&
        defaultQuestions.length === 1 &&
        String(defaultQuestions[0]?.id) === String(NAME_QUESTION_SEED.id);
      const isCurrentDefaultEmpty = !Array.isArray(defaultQuestions) || defaultQuestions.length === 0 || hasOnlyNameSeed;

      if (isCurrentDefaultEmpty && templateDefaultQuestions.length > 0) {
        setDefaultQuestions(cloneQuestions(templateDefaultQuestions));
      }

      applyEditorStructure(nextQuestions, questionPages);
      setTemplateName(template.name || '');
      if (error) setError(null);
      showNotification('Vorlage angewendet: Leere Seiten wurden ergänzt');
    } catch (templateApplyError) {
      console.error('Error applying template to empty pages:', templateApplyError);
      setError('Fehler beim Anwenden der Vorlage');
    }
  };

  // Renames an existing template in place instead of saving a new record.
  // Returns true on success so the UI can close its inline rename editor only
  // when the rename actually went through.
  const renameTemplateById = async (templateId, rawName) => {
    const newName = (rawName || '').trim();
    if (!templateId) return false;
    if (!newName) {
      setError('Bitte geben Sie einen Vorlagennamen ein.');
      return false;
    }

    try {
      const allTemplates = await dbService.getTemplates();
      const target = allTemplates.find((t) => String(t.id) === String(templateId));
      if (!target) return false;

      // No-op rename — nothing to do.
      if ((target.name || '').trim() === newName) return true;

      const collision = allTemplates.find(
        (t) => String(t.id) !== String(templateId) && (t.name || '').trim().toLowerCase() === newName.toLowerCase()
      );
      if (collision) {
        setError(`Eine Vorlage mit dem Namen "${newName}" existiert bereits.`);
        return false;
      }

      await dbService.renameTemplate(templateId, newName);
      const updatedTemplates = await dbService.getTemplates();
      setTemplates(updatedTemplates);
      // Keep the name input in sync — the selected template is the loaded one.
      if (String(selectedTemplateId) === String(templateId)) {
        setTemplateName(newName);
      }
      if (error) setError(null);
      showNotification(`Vorlage in "${newName}" umbenannt`, 'success');
      return true;
    } catch (renameError) {
      console.error('Error renaming template:', renameError);
      setError(`Fehler beim Umbenennen der Vorlage: ${renameError.message}`);
      return false;
    }
  };

  // Toggles the archive state of a template. Selection is kept so the
  // Archivieren/Wiederherstellen button flips in place.
  const toggleTemplateArchivedById = async (templateId) => {
    if (!templateId) return;

    try {
      const target = templates.find((t) => String(t.id) === String(templateId));
      if (!target) return;

      const nextArchived = !target.archived;
      await dbService.setTemplateArchived(templateId, nextArchived);

      const updatedTemplates = await dbService.getTemplates();
      setTemplates(updatedTemplates);
      if (error) setError(null);
      showNotification(nextArchived ? 'Vorlage archiviert' : 'Vorlage wiederhergestellt', 'success');
    } catch (archiveError) {
      console.error('Error toggling template archive state:', archiveError);
      setError('Fehler beim Archivieren der Vorlage');
    }
  };

  const deleteTemplateById = async (templateId) => {
    if (!templateId) return;

    try {
      const allTemplates = await dbService.getTemplates();
      const template = allTemplates.find(t => t.id === templateId);

      if (!template) return;

      const confirmDelete = window.confirm(`Vorlage "${template.name}" löschen?`);
      if (!confirmDelete) return;

      await dbService.deleteTemplate(templateId);

      const updatedTemplates = await dbService.getTemplates();
      setTemplates(updatedTemplates);
      setSelectedTemplateId('');
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Fehler beim Löschen der Vorlage');
    }
  };

  // --- Basic question template CRUD ---
  const saveBasicQuestionTemplate = async () => {
    if (!isBasisPageSelected || !activePage) {
      setError('Basisfragen-Vorlagen können nur auf einer Basisfragen-Seite gespeichert werden.');
      return;
    }
    if (!basicTemplateName.trim()) {
      setError('Bitte geben Sie einen Vorlagennamen für Basisfragen ein.');
      return;
    }

    const basisQuestions = questions.filter((question) => question.pageId === activePage.id);
    if (basisQuestions.length === 0) {
      setError('Bitte fügen Sie zuerst Basisfragen hinzu.');
      return;
    }

    const portableQuestions = basisQuestions.map((question) => {
      const portableQuestion = { ...(question || {}) };
      delete portableQuestion.file;
      delete portableQuestion.pageId;
      delete portableQuestion.pageTitle;
      delete portableQuestion.pageDescription;
      delete portableQuestion.pageOrder;
      return portableQuestion;
    });

    try {
      const existingTemplate = basicQuestionTemplates.find(
        (template) => template.name?.trim().toLowerCase() === basicTemplateName.trim().toLowerCase()
      );

      if (existingTemplate) {
        const confirmOverwrite = window.confirm(`Eine Basisfragen-Vorlage mit dem Namen "${basicTemplateName}" existiert bereits. Überschreiben?`);
        if (!confirmOverwrite) return;
        await dbService.updateBasicQuestionTemplate(existingTemplate.id, {
          name: basicTemplateName.trim(),
          questions: portableQuestions
        });
      } else {
        await dbService.saveBasicQuestionTemplate({
          name: basicTemplateName.trim(),
          questions: portableQuestions
        });
      }

      const updatedTemplates = await dbService.getBasicQuestionTemplates();
      setBasicQuestionTemplates(updatedTemplates);
      setBasicTemplateName('');
      showNotification('Basisfragen-Vorlage gespeichert');
    } catch (error) {
      console.error('Error saving basic question template:', error);
      setError(`Fehler beim Speichern der Basisfragen-Vorlage: ${error.message}`);
    }
  };

  const loadBasicQuestionTemplate = async (templateId) => {
    if (!templateId || !activePage) return;
    if (!isBasisPageSelected) {
      setError('Basisfragen-Vorlagen können nur auf einer Basisfragen-Seite geladen werden.');
      return;
    }

    try {
      const allTemplates = await dbService.getBasicQuestionTemplates();
      const template = allTemplates.find((entry) => entry.id === templateId);
      if (!template) return;

      const mappedQuestions = remapTemplateQuestionsToPage(template.questions || [], activePage.id);
      const remainingQuestions = questions.filter((question) => question.pageId !== activePage.id);
      applyEditorStructure([...remainingQuestions, ...mappedQuestions], questionPages);
      setBasicTemplateName(template.name || '');
      if (error) setError(null);
    } catch (error) {
      console.error('Error loading basic question template:', error);
      setError('Fehler beim Laden der Basisfragen-Vorlage');
    }
  };

  const deleteBasicQuestionTemplateById = async (templateId) => {
    if (!templateId) return;
    try {
      const template = basicQuestionTemplates.find((entry) => entry.id === templateId);
      const confirmDelete = window.confirm(`Basisfragen-Vorlage "${template?.name || 'Unbenannt'}" löschen?`);
      if (!confirmDelete) return;

      await dbService.deleteBasicQuestionTemplate(templateId);
      const updatedTemplates = await dbService.getBasicQuestionTemplates();
      setBasicQuestionTemplates(updatedTemplates);
      setSelectedBasicTemplateId('');
      showNotification('Basisfragen-Vorlage gelöscht');
    } catch (error) {
      console.error('Error deleting basic question template:', error);
      setError('Fehler beim Löschen der Basisfragen-Vorlage');
    }
  };

  // --- Fach question template CRUD ---
  const saveFachQuestionTemplate = async () => {
    if (!isFachPageSelected || !activePage) {
      setError('Fachspezifische Vorlagen können nur auf einer Fachspezifisch-Seite gespeichert werden.');
      return;
    }
    if (!fachTemplateName.trim()) {
      setError('Bitte geben Sie einen Vorlagennamen für fachspezifische Fragen ein.');
      return;
    }

    const fachQuestions = questions.filter((question) => question.pageId === activePage.id);
    if (fachQuestions.length === 0) {
      setError('Bitte fügen Sie zuerst fachspezifische Fragen hinzu.');
      return;
    }

    const portableQuestions = fachQuestions.map((question) => {
      const portableQuestion = { ...(question || {}) };
      delete portableQuestion.file;
      delete portableQuestion.pageId;
      delete portableQuestion.pageTitle;
      delete portableQuestion.pageDescription;
      delete portableQuestion.pageOrder;
      return portableQuestion;
    });

    try {
      const existingTemplate = fachQuestionTemplates.find(
        (template) => template.name?.trim().toLowerCase() === fachTemplateName.trim().toLowerCase()
      );

      if (existingTemplate) {
        const confirmOverwrite = window.confirm(`Eine fachspezifische Vorlage mit dem Namen "${fachTemplateName}" existiert bereits. Überschreiben?`);
        if (!confirmOverwrite) return;
        await dbService.updateFachQuestionTemplate(existingTemplate.id, {
          name: fachTemplateName.trim(),
          questions: portableQuestions
        });
      } else {
        await dbService.saveFachQuestionTemplate({
          name: fachTemplateName.trim(),
          questions: portableQuestions
        });
      }

      const updatedTemplates = await dbService.getFachQuestionTemplates();
      setFachQuestionTemplates(updatedTemplates);
      setFachTemplateName('');
      showNotification('Fachspezifische Vorlage gespeichert');
    } catch (error) {
      console.error('Error saving fach question template:', error);
      setError(`Fehler beim Speichern der fachspezifischen Vorlage: ${error.message}`);
    }
  };

  const loadFachQuestionTemplate = async (templateId) => {
    if (!templateId || !activePage) return;
    if (!isFachPageSelected) {
      setError('Fachspezifische Vorlagen können nur auf einer Fachspezifisch-Seite geladen werden.');
      return;
    }

    try {
      const allTemplates = await dbService.getFachQuestionTemplates();
      const template = allTemplates.find((entry) => entry.id === templateId);
      if (!template) return;

      const mappedQuestions = remapTemplateQuestionsToPage(template.questions || [], activePage.id);
      const remainingQuestions = questions.filter((question) => question.pageId !== activePage.id);
      applyEditorStructure([...remainingQuestions, ...mappedQuestions], questionPages);
      setFachTemplateName(template.name || '');
      if (error) setError(null);
    } catch (error) {
      console.error('Error loading fach question template:', error);
      setError('Fehler beim Laden der fachspezifischen Vorlage');
    }
  };

  const deleteFachQuestionTemplateById = async (templateId) => {
    if (!templateId) return;
    try {
      const template = fachQuestionTemplates.find((entry) => entry.id === templateId);
      const confirmDelete = window.confirm(`Fachspezifische Vorlage "${template?.name || 'Unbenannt'}" löschen?`);
      if (!confirmDelete) return;

      await dbService.deleteFachQuestionTemplate(templateId);
      const updatedTemplates = await dbService.getFachQuestionTemplates();
      setFachQuestionTemplates(updatedTemplates);
      setSelectedFachTemplateId('');
      showNotification('Fachspezifische Vorlage gelöscht');
    } catch (error) {
      console.error('Error deleting fach question template:', error);
      setError('Fehler beim Löschen der fachspezifischen Vorlage');
    }
  };

  // --- Default template configuration ---
  const setFormalTemplateAsDefault = () => {
    if (!selectedDefaultTemplateId) {
      setError('Bitte zuerst eine Vorlage für formale Fragen auswählen.');
      return;
    }
    const saved = setConfiguredTemplateId(DEFAULT_FORMAL_TEMPLATE_ID_STORAGE_KEY, selectedDefaultTemplateId);
    if (!saved) {
      setError('Standard-Vorlage für formale Fragen konnte nicht gespeichert werden.');
      return;
    }
    setDefaultFormalTemplateId(selectedDefaultTemplateId);
    showNotification('Standard-Vorlage für formale Fragen gesetzt');
  };

  const resetFormalTemplateDefault = () => {
    clearConfiguredTemplateId(DEFAULT_FORMAL_TEMPLATE_ID_STORAGE_KEY);
    setDefaultFormalTemplateId('');
    showNotification('Standard-Vorlage für formale Fragen zurückgesetzt');
  };

  const setBasisTemplateAsDefault = () => {
    if (!selectedBasicTemplateId) {
      setError('Bitte zuerst eine Vorlage für Basisfragen auswählen.');
      return;
    }
    const saved = setConfiguredTemplateId(DEFAULT_BASIS_TEMPLATE_ID_STORAGE_KEY, selectedBasicTemplateId);
    if (!saved) {
      setError('Standard-Vorlage für Basisfragen konnte nicht gespeichert werden.');
      return;
    }
    setDefaultBasisTemplateId(selectedBasicTemplateId);
    showNotification('Standard-Vorlage für Basisfragen gesetzt');
  };

  const resetBasisTemplateDefault = () => {
    clearConfiguredTemplateId(DEFAULT_BASIS_TEMPLATE_ID_STORAGE_KEY);
    setDefaultBasisTemplateId('');
    showNotification('Standard-Vorlage für Basisfragen zurückgesetzt');
  };

  const setFachTemplateAsDefault = () => {
    if (!selectedFachTemplateId) {
      setError('Bitte zuerst eine Vorlage für fachspezifische Fragen auswählen.');
      return;
    }
    const saved = setConfiguredTemplateId(DEFAULT_FACH_TEMPLATE_ID_STORAGE_KEY, selectedFachTemplateId);
    if (!saved) {
      setError('Standard-Vorlage für fachspezifische Fragen konnte nicht gespeichert werden.');
      return;
    }
    setDefaultFachTemplateId(selectedFachTemplateId);
    showNotification('Standard-Vorlage für fachspezifische Fragen gesetzt');
  };

  const resetFachTemplateDefault = () => {
    clearConfiguredTemplateId(DEFAULT_FACH_TEMPLATE_ID_STORAGE_KEY);
    setDefaultFachTemplateId('');
    showNotification('Standard-Vorlage für fachspezifische Fragen zurückgesetzt');
  };

  // --- Reset editor to defaults ---
  const resetEditorToDefaults = useCallback(async () => {
    try {
      const defaultPages = createDefaultCustomPages();
      const basisPage = defaultPages.find((page) => normalizeCustomPageType(page.pageType, PAGE_TYPE_FACH) === PAGE_TYPE_BASIS) || defaultPages[0];
      const fachPage = defaultPages.find((page) => normalizeCustomPageType(page.pageType, PAGE_TYPE_FACH) === PAGE_TYPE_FACH);
      let nextQuestions = [];

      if (basisPage && defaultBasisTemplateId) {
        let templateSource = basicQuestionTemplates;
        if (!templateSource.some((entry) => String(entry.id) === String(defaultBasisTemplateId))) {
          templateSource = await dbService.getBasicQuestionTemplates();
          setBasicQuestionTemplates(templateSource);
        }
        const template = templateSource.find((entry) => String(entry.id) === String(defaultBasisTemplateId));
        if (template) {
          nextQuestions = remapTemplateQuestionsToPage(template.questions || [], basisPage.id);
          setSelectedBasicTemplateId(template.id);
          setBasicTemplateName(template.name || '');
        } else {
          setSelectedBasicTemplateId('');
          setBasicTemplateName('');
        }
      } else {
        setSelectedBasicTemplateId('');
        setBasicTemplateName('');
      }

      if (fachPage && defaultFachTemplateId) {
        let templateSource = fachQuestionTemplates;
        if (!templateSource.some((entry) => String(entry.id) === String(defaultFachTemplateId))) {
          templateSource = await dbService.getFachQuestionTemplates();
          setFachQuestionTemplates(templateSource);
        }
        const template = templateSource.find((entry) => String(entry.id) === String(defaultFachTemplateId));
        if (template) {
          nextQuestions = [...nextQuestions, ...remapTemplateQuestionsToPage(template.questions || [], fachPage.id)];
          setSelectedFachTemplateId(template.id);
          setFachTemplateName(template.name || '');
        } else {
          setSelectedFachTemplateId('');
          setFachTemplateName('');
        }
      } else {
        setSelectedFachTemplateId('');
        setFachTemplateName('');
      }

      let nextDefaultQuestions = [];
      if (defaultFormalTemplateId) {
        let templateSource = defaultQuestionTemplates;
        if (!templateSource.some((entry) => String(entry.id) === String(defaultFormalTemplateId))) {
          templateSource = await dbService.getDefaultQuestionTemplates();
          setDefaultQuestionTemplates(templateSource);
        }
        const template = templateSource.find((entry) => String(entry.id) === String(defaultFormalTemplateId));
        if (template?.questions && Array.isArray(template.questions)) {
          nextDefaultQuestions = cloneQuestions(template.questions);
          setSelectedDefaultTemplateId(template.id);
          setDefaultTemplateName(template.name || '');
        } else {
          setSelectedDefaultTemplateId('');
          setDefaultTemplateName('');
        }
      } else {
        setSelectedDefaultTemplateId('');
        setDefaultTemplateName('');
      }

      setTitle('');
      applyEditorStructure(nextQuestions, defaultPages);
      setIntroText(defaultIntroText);
      setOutroText(defaultOutroText);
      setIntroWelcomeText('');
      setIntroOverviewText('');
      setIntroAfterSectionsText('');
      setDefaultSectionTitle('Formale Fragen');
      setDefaultSectionDescription('');
      setReviewSectionTitle('Überprüfung');
      setReviewSectionDescription('');
      setIntroSectionsText('');
      setIntroTimerText('');
      setIntroStartButtonText('');
      setAllowMultipleSubmissions(false);
      setScoringPrompt('');
      if (setScoringTotalScore) setScoringTotalScore(400);
      if (setPageMaxScores) setPageMaxScores({});
      setDefaultQuestions(nextDefaultQuestions.length > 0 ? nextDefaultQuestions : [NAME_QUESTION_SEED]);
      setDefaultQuestionsPageOrder(clampDefaultQuestionsPageOrder(defaultPages.length + 1, defaultPages.length));
      setTemplateName('');
      setSelectedTemplateId('');
      if (error) setError(null);
    } catch (resetError) {
      console.error('Error resetting editor to defaults:', resetError);
      setError('Fehler beim Zurücksetzen auf Standardwerte');
    }
  }, [
    defaultBasisTemplateId,
    basicQuestionTemplates,
    defaultFachTemplateId,
    fachQuestionTemplates,
    defaultFormalTemplateId,
    defaultQuestionTemplates,
    defaultIntroText,
    defaultOutroText,
    applyEditorStructure,
    error,
    remapTemplateQuestionsToPage,
    setError,
    setTitle,
    setIntroText,
    setOutroText,
    setIntroWelcomeText,
    setIntroOverviewText,
    setIntroAfterSectionsText,
    setDefaultSectionTitle,
    setDefaultSectionDescription,
    setReviewSectionTitle,
    setReviewSectionDescription,
    setIntroSectionsText,
    setIntroTimerText,
    setIntroStartButtonText,
    setAllowMultipleSubmissions,
    setScoringPrompt,
    setScoringTotalScore,
    setPageMaxScores,
    setDefaultQuestions,
    setDefaultQuestionsPageOrder
  ]);

  // Auto-load default templates on mount
  useEffect(() => {
    if (hasAutoLoadedDefaultTemplatesRef.current) return;
    if (!defaultFormalTemplateId && !defaultBasisTemplateId && !defaultFachTemplateId) return;
    if (defaultFormalTemplateId && defaultQuestionTemplates.length === 0) return;
    if (defaultBasisTemplateId && basicQuestionTemplates.length === 0) return;
    if (defaultFachTemplateId && fachQuestionTemplates.length === 0) return;

    hasAutoLoadedDefaultTemplatesRef.current = true;
    resetEditorToDefaults();
  }, [
    defaultFormalTemplateId,
    defaultBasisTemplateId,
    defaultFachTemplateId,
    defaultQuestionTemplates,
    basicQuestionTemplates,
    fachQuestionTemplates,
    resetEditorToDefaults
  ]);

  // --- Default Questions Template CRUD ---
  const saveDefaultQuestionTemplate = async () => {
    if (!defaultTemplateName.trim() || defaultQuestions.length === 0) {
      setError("Bitte geben Sie einen Vorlagennamen ein und fügen Sie formale Fragen hinzu.");
      return;
    }

    try {
      const allTemplates = await dbService.getDefaultQuestionTemplates();
      const existingTemplate = allTemplates.find(t => t.name.trim().toLowerCase() === defaultTemplateName.trim().toLowerCase());

      if (existingTemplate) {
        const confirmOverwrite = window.confirm(`Eine Vorlage mit dem Namen "${defaultTemplateName}" existiert bereits. Möchten Sie diese überschreiben?`);
        if (!confirmOverwrite) return;

        await dbService.updateDefaultQuestionTemplate(existingTemplate.id, {
          name: defaultTemplateName.trim(),
          questions: defaultQuestions
        });
      } else {
        await dbService.saveDefaultQuestionTemplate({
          name: defaultTemplateName.trim(),
          questions: defaultQuestions
        });
      }

      const updatedTemplates = await dbService.getDefaultQuestionTemplates();
      setDefaultQuestionTemplates(updatedTemplates);
      setDefaultTemplateName('');
      showNotification('Vorlage für formale Fragen gespeichert');
    } catch (error) {
      console.error('Error saving default question template:', error);
      setError(`Fehler beim Speichern der Vorlage für formale Fragen: ${error.message}`);
    }
  };

  const loadDefaultQuestionTemplate = async (templateId) => {
    try {
      const allTemplates = await dbService.getDefaultQuestionTemplates();
      const template = allTemplates.find(t => t.id === templateId);

      if (template) {
        const loaded = template.questions || [];
        if (loaded.length > 0) {
          const hasNameQuestion = loaded.some((question) => question?.id === NAME_QUESTION_SEED.id);
          setDefaultQuestions(hasNameQuestion ? loaded : [NAME_QUESTION_SEED, ...loaded]);
        } else {
          setDefaultQuestions([NAME_QUESTION_SEED]);
        }
        setDefaultTemplateName(template.name);
        if (error) setError(null);
      }
    } catch (error) {
      console.error('Error loading default question template:', error);
      setError('Fehler beim Laden der Vorlage für formale Fragen');
    }
  };

  const deleteDefaultQuestionTemplateById = async (templateId) => {
    if (!templateId) return;

    try {
      const allTemplates = await dbService.getDefaultQuestionTemplates();
      const template = allTemplates.find(t => t.id === templateId);

      if (!template) return;

      setPendingDefaultTemplateData({ template, templateId });
      setShowDefaultTemplateConfirmModal(true);
    } catch (error) {
      console.error('Error preparing default question template deletion:', error);
      setError('Fehler beim Vorbereiten des Löschens der Vorlage für formale Fragen');
    }
  };

  const confirmDeleteDefaultQuestionTemplate = async () => {
    if (!pendingDefaultTemplateData) return;

    try {
      await dbService.deleteDefaultQuestionTemplate(pendingDefaultTemplateData.templateId);

      const updatedTemplates = await dbService.getDefaultQuestionTemplates();
      setDefaultQuestionTemplates(updatedTemplates);

      setSelectedDefaultTemplateId('');
      setShowDefaultTemplateConfirmModal(false);
      setPendingDefaultTemplateData(null);
      showNotification('Vorlage für formale Fragen gelöscht');
    } catch (error) {
      console.error('Error deleting default question template:', error);
      setError('Fehler beim Löschen der Vorlage für formale Fragen');
      setShowDefaultTemplateConfirmModal(false);
      setPendingDefaultTemplateData(null);
    }
  };

  return {
    // Questionnaire templates
    templates,
    templateName, setTemplateName,
    selectedTemplateId, setSelectedTemplateId,
    saveAsTemplate, saveTemplateData, loadFromTemplate, applyTemplateToEmptyPages, deleteTemplateById,
    renameTemplateById,
    toggleTemplateArchivedById,
    // Basic question templates
    basicQuestionTemplates,
    basicTemplateName, setBasicTemplateName,
    selectedBasicTemplateId, setSelectedBasicTemplateId,
    saveBasicQuestionTemplate, loadBasicQuestionTemplate, deleteBasicQuestionTemplateById,
    // Fach question templates
    fachQuestionTemplates,
    fachTemplateName, setFachTemplateName,
    selectedFachTemplateId, setSelectedFachTemplateId,
    saveFachQuestionTemplate, loadFachQuestionTemplate, deleteFachQuestionTemplateById,
    // Default/formal question templates
    defaultQuestionTemplates,
    defaultTemplateName, setDefaultTemplateName,
    selectedDefaultTemplateId, setSelectedDefaultTemplateId,
    saveDefaultQuestionTemplate, loadDefaultQuestionTemplate,
    deleteDefaultQuestionTemplateById, confirmDeleteDefaultQuestionTemplate,
    // Default template configuration
    defaultFormalTemplateId, setDefaultFormalTemplateId,
    defaultBasisTemplateId, setDefaultBasisTemplateId,
    defaultFachTemplateId, setDefaultFachTemplateId,
    setFormalTemplateAsDefault, resetFormalTemplateDefault,
    setBasisTemplateAsDefault, resetBasisTemplateDefault,
    setFachTemplateAsDefault, resetFachTemplateDefault,
    // Template modals
    showTemplateConfirmModal, setShowTemplateConfirmModal,
    pendingTemplateData, setPendingTemplateData,
    showDefaultTemplateConfirmModal, setShowDefaultTemplateConfirmModal,
    pendingDefaultTemplateData, setPendingDefaultTemplateData,
    // Reset
    resetEditorToDefaults,
    // Computed
    configuredFormalTemplateName: defaultQuestionTemplates.find((template) => String(template.id) === String(defaultFormalTemplateId))?.name || '',
    configuredBasisTemplateName: basicQuestionTemplates.find((template) => String(template.id) === String(defaultBasisTemplateId))?.name || '',
    configuredFachTemplateName: fachQuestionTemplates.find((template) => String(template.id) === String(defaultFachTemplateId))?.name || ''
  };
}
